import React, { FC, useState } from 'react';
import { Button, Input } from '../../components';
import { ConfigProvider } from 'antd';
import { SiPayModal } from 'utilities/components';
import { DatePicker, JalaliLocaleListener } from 'antd-jalali';
import { convertFaToEnCalendar2, faNumToEnNum, post } from '../../../scripts';
import fa_IR from 'antd/locale/fa_IR';
import dayjs from 'dayjs';
import { LevelOne } from 'interfaces';
import { API } from 'data/30pay.api';
import moment from 'jalali-moment';
import { verifyUpgradeTo1 } from '30pay/profile/verify/modal/verifyModal.service';
import { toast } from 'react-toastify';

const requestUpgradeTo1 = async (
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
    data: { nationalCode?: string; birthDate?: string },
    setData: React.Dispatch<React.SetStateAction<LevelOne | undefined>>,
    onComplete?: (e: any) => void,
): Promise<boolean> => {
    setLoading(true);
    try {
        const response = await post<LevelOne>(API.level.requestUpgradeTo1, data);
        setData(response.value.value);
        onComplete && onComplete(response.value.value);
        return true;
    } catch (err) {
        console.log(err);
        return false;
    } finally {
        setLoading(false);
    }
};

interface Props {
    onComplete: () => void;
    onSkip: () => void;
}

export const EnterUserInfo: FC<Props> = ({ onComplete, onSkip }) => {
    const [nationalCode, setNationalCode] = useState<string>('');
    const [birthDate, setBirthDate] = useState<dayjs.Dayjs | null>(null);
    const [cardNumber, setCardNumber] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);

    const [verifyData, setVerifyData] = useState<LevelOne | undefined>();
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState<boolean>(false);
    const [verifyLoading, setVerifyLoading] = useState<boolean>(false);

    const handleSubmit = async () => {
        const jalaliDate = birthDate?.format('YYYY/MM/DD');

        const payload = {
            nationalCode: faNumToEnNum(nationalCode),
            birthDate: jalaliDate ? convertFaToEnCalendar2(jalaliDate) : undefined,
        };

        // normalize card number and check that it's fully and correctly filled
        const parsedCardNumber = faNumToEnNum(cardNumber).replace(/\D/g, '').slice(0, 16);
        const shouldSendCardNumber = !!parsedCardNumber && parsedCardNumber.length === 16;

        const afterRequest = (e: LevelOne) => {
            if (e.isWaitingForAdmin &&
                e.isWaitingForAdmin === true) {
                toast.success('لطفا منتظر تایید باشید.')
                setIsVerifyModalOpen(false);
            }
            else
                setIsVerifyModalOpen(true);
        };

        try {
            const isSuccess = await requestUpgradeTo1(setLoading, payload, setVerifyData, afterRequest);

            if (isSuccess && shouldSendCardNumber) {
                const body = { cardNumber: parsedCardNumber, addIBan: false };
                await post(API.bankAccount.addCardNumber, body).catch((e) => console.log(e));
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleVerifyConfirm = () => {
        setVerifyLoading(true);
        verifyUpgradeTo1({ isConfirmed: true })
            .then(() => {
                setIsVerifyModalOpen(false);
                onComplete();
            })
            .finally(() => setVerifyLoading(false));
    };

    // very simple validations – tweak if backend rules differ
    const isNationalCodeValid = nationalCode && faNumToEnNum(nationalCode).length === 10;
    const isBirthDateValid = !!birthDate;
    const isCardValid = !cardNumber || faNumToEnNum(cardNumber).length === 16; // optional, but if filled must be 16 digits

    const isButtonDisabled = loading || !isNationalCodeValid || !isBirthDateValid || !isCardValid;

    return (
        <>
            <SiPayModal
                isOpen={isVerifyModalOpen}
                onClose={() => setIsVerifyModalOpen(false)}
                title="تایید اطلاعات کاربری"
            >
                {verifyData && (
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-2 gap-4">
                            {verifyData.nationalCode && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">کد ملی:</span>
                                    <span className="font-medium text-base text-black-60">
                                        {verifyData.nationalCode}
                                    </span>
                                </div>
                            )}
                            {verifyData.fullName && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">نام و نام خانوادگی:</span>
                                    <span className="font-medium text-base text-black-60">{verifyData.fullName}</span>
                                </div>
                            )}
                            {verifyData.fatherName && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">نام پدر:</span>
                                    <span className="font-medium text-base text-black-60">{verifyData.fatherName}</span>
                                </div>
                            )}
                            {verifyData.birthDate && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">تاریخ تولد:</span>
                                    <span className="font-medium text-base text-black-60">
                                        {moment.utc(verifyData.birthDate).locale('fa').local().format('jD jMMMM jYYYY')}
                                    </span>
                                </div>
                            )}
                            {verifyData.gender && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">جنسیت:</span>
                                    <span className="font-medium text-base text-black-60">{verifyData.gender}</span>
                                </div>
                            )}
                            {verifyData.email && (
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-base text-black-100">ایمیل:</span>
                                    <span className="font-medium text-base text-black-60">{verifyData.email}</span>
                                </div>
                            )}
                        </div>
                        <div className="flex items-center gap-4 w-full">
                            <Button
                                type="primary"
                                className="w-full"
                                loading={verifyLoading}
                                onClick={handleVerifyConfirm}
                            >
                                تایید
                            </Button>
                            <Button
                                type="text"
                                className="bg-background w-full"
                                onClick={() => setIsVerifyModalOpen(false)}
                            >
                                انصراف
                            </Button>
                        </div>
                    </div>
                )}
            </SiPayModal>
            <div className={'flex flex-col w-full'}>
                <p className={'text-xs text-darkPurple-100 mt-6 font-normal'}>
                    کد ملی و تاریخ تولد خود را در کادرهای زیر وارد کنید.
                </p>

                <Input
                    className={'mt-6'}
                    placeholder={'کد ملی'}
                    value={nationalCode}
                    dir={nationalCode ? 'ltr' : 'rtl'}
                    maxLength={10}
                    inputMode={'numeric'}
                    onChange={(e) => setNationalCode(faNumToEnNum(e.target.value))}
                />

                <div className="mt-4">
                    <ConfigProvider locale={fa_IR} direction={'rtl'}>
                        <JalaliLocaleListener />
                        <DatePicker
                            className={
                                'mt-2 border border-primary-40 rounded-[28px] h-14 max-lg:h-12 text-base max-lg:text-xs px-3 w-full'
                            }
                            placeholder={'تاریخ تولد'}
                            value={birthDate as any}
                            onChange={(date: any) => {
                                if (date !== null) {
                                    setBirthDate(date);
                                } else {
                                    setBirthDate(null);
                                }
                            }}
                        />
                    </ConfigProvider>
                </div>

                {/* شماره کارت بانکی (اختیاری) */}
                <label className={'text-sm mt-6 font-light max-lg:text-xs'}>شماره کارت بانکی (اختیاری):</label>
                <Input
                    className={'mt-3'}
                    placeholder={'شماره کارت بانکی'}
                    value={cardNumber}
                    dir={cardNumber ? 'ltr' : 'rtl'}
                    maxLength={16}
                    inputMode={'numeric'}
                    onChange={(e) => setCardNumber(faNumToEnNum(e.target.value))}
                />

                <Button
                    className={'mt-6'}
                    type={'primary'}
                    loading={loading}
                    disabled={isButtonDisabled}
                    onClick={handleSubmit}
                >
                    ارسال اطلاعات
                </Button>

                <Button className={'bg-white text-primary-100 mt-2'} type={'text'} onClick={onSkip}>
                    بعدا انجام می‌دهم
                </Button>
            </div>
        </>
    );
};

export default EnterUserInfo;
